import {
  DateTime,
} from 'luxon';

import {
  aggregator_user_data_repository,
  query_repository,
} from '@/repositories';
import {
  MY_LIST_CONSTANTS,
  QUERY_CONSTANTS,
} from '@/constants';

const ENTITY_PAGE_SIZE = 20;
const QUICKSEARCH_MIN_LENGTH = 3;
const REMOVE_CONTACT_REFRESH_DELAY = 1000;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const MATCHED_QUERIES_QUERY_TYPE_ID_LIST = [
  QUERY_CONSTANTS.TYPES.IGNORE_ENGAGE_CONTACT_LIST,
  QUERY_CONSTANTS.TYPES.IGNORE_CAMPAIGN_CONTACT_LIST,
  QUERY_CONSTANTS.TYPES.IGNORE_CUSTOM_CONTACT_LIST,
  QUERY_CONSTANTS.TYPES.INTERACTION_MAIL_BOUNCED,
  QUERY_CONSTANTS.TYPES.PROSPECTING_CONTACT,
  QUERY_CONSTANTS.TYPES.INTERACTION_MAIL_AUTOREPLIED,
];

const IGNORE_QUERY_TYPE_ID_LIST = [
  QUERY_CONSTANTS.TYPES.IGNORE_ALL_CONTACT_LIST,
  QUERY_CONSTANTS.TYPES.IGNORE_ENGAGE_CONTACT_LIST,
  QUERY_CONSTANTS.TYPES.IGNORE_CAMPAIGN_CONTACT_LIST,
  QUERY_CONSTANTS.TYPES.IGNORE_CUSTOM_CONTACT_LIST,
];

const parseQueryCriteria = (criteria) => {
  if (!criteria) {
    return {};
  }

  if (typeof criteria === 'string') {
    return JSON.parse(criteria);
  }

  return { ...criteria };
};

const criteriaContactIdKey = (criteria) => {
  if (Array.isArray(criteria.person_id_list)) {
    return 'person_id_list';
  }

  if (Array.isArray(criteria.contact_id_list)) {
    return 'contact_id_list';
  }

  return null;
};

const isIgnoreList = (query_information) => {
  const query_type_id = Number(
    query_information?.query_type?.id ?? query_information?.query_type_id
  );

  return IGNORE_QUERY_TYPE_ID_LIST.includes(query_type_id);
};

const isQueryShared = (query_information) => {
  const query_sharing_list = query_information?.query_sharing_list;

  if (!query_sharing_list) {
    return false;
  }

  return query_sharing_list.length > 1;
};

const isListModified = (query_information) => {
  if (!query_information) {
    return false;
  }

  return query_information.creation_datetime !== query_information.modification_date;
};

const resolveEntityType = (query_type_id) => {
  const type_identifier = Number(query_type_id);

  if (MY_LIST_CONSTANTS.CONTACT_QUERY_TYPE_ID_LIST.includes(type_identifier)) {
    return MY_LIST_CONSTANTS.ENTITY_TYPES.CONTACT;
  }

  if (MY_LIST_CONSTANTS.COMPANY_QUERY_TYPE_ID_LIST.includes(type_identifier)) {
    return MY_LIST_CONSTANTS.ENTITY_TYPES.COMPANY;
  }

  return null;
};

const parseOptionDate = (date) => (
  DATE_ONLY_PATTERN.test(String(date).trim())
    ? DateTime.fromISO(date)
    : DateTime.fromISO(date, { zone: 'utc' })
);

const contactIdListFromResponse = (response) => (
  Array.isArray(response?.contact_id_list)
    ? response.contact_id_list
    : []
);

const companyIdListFromResponse = (response) => (
  Array.isArray(response?.company_id_list)
    ? response.company_id_list
    : []
);

const fetchAllCompanyIds = async (state) => {
  const response = await aggregator_user_data_repository.searchCompanyIds(
    buildAccountSearchCriteria(state, {
      limit: state.account_count,
    })
  );

  return companyIdListFromResponse(response);
};

const fetchAllContactIds = async (state) => {
  const response = await aggregator_user_data_repository.searchContactIds(
    buildContactSearchCriteria(state, {
      limit: state.contact_count,
      with_matched_queries: false,
    })
  );

  return contactIdListFromResponse(response);
};

const buildAccountSearchCriteria = (state, {
  limit,
  offset,
  with_matched_queries = false,
}) => ({
  ...state.search_query,
  limit,
  ...(
    with_matched_queries
      ? { matched_queries_query_type_id_list: MATCHED_QUERIES_QUERY_TYPE_ID_LIST }
      : {}
  ),
  ...(offset === undefined ? {} : { offset }),
  ...(
    state.account_sort_list.length > 0
      ? { sort: state.account_sort_list }
      : {}
  ),
  ...(
    state.account_quicksearch.length >= QUICKSEARCH_MIN_LENGTH
      ? { company_name_list: [state.account_quicksearch] }
      : {}
  ),
  ...(
    state.bookmark_filter_enabled
      ? { bookmark: true }
      : {}
  ),
  ...state.account_column_filter_by_field,
});

const buildContactSearchCriteria = (state, {
  limit,
  offset,
  with_matched_queries = true,
}) => ({
  ...state.search_query,
  limit,
  ...(
    with_matched_queries
      ? { matched_queries_query_type_id_list: MATCHED_QUERIES_QUERY_TYPE_ID_LIST }
      : {}
  ),
  ...(offset === undefined ? {} : { offset }),
  ...(
    state.contact_sort_list.length > 0
      ? { sort: state.contact_sort_list }
      : {}
  ),
  ...(
    state.contact_quicksearch.length >= QUICKSEARCH_MIN_LENGTH
      ? { contact_quicksearch_list: [state.contact_quicksearch] }
      : {}
  ),
  ...(
    state.bookmark_filter_enabled
      ? { bookmark: true }
      : {}
  ),
  ...state.contact_column_filter_by_field,
});

const buildColumnValueCriteria = (criteria, column, value) => {
  const column_value_criteria = {
    ...criteria,
    column,
  };
  const normalized_value = typeof value === 'string' ? value.trim() : '';

  delete column_value_criteria.limit;
  delete column_value_criteria.offset;
  delete column_value_criteria.sort;

  if (normalized_value !== '') {
    column_value_criteria.value = normalized_value;
  }

  return column_value_criteria;
};

const columnFilterByField = (filter_by_field, field, value_list) => {
  const next_filter_by_field = {
    ...filter_by_field,
  };
  const next_value_list = (value_list || []).filter((value) => value !== '');

  if (next_value_list.length === 0) {
    delete next_filter_by_field[field];
  } else {
    next_filter_by_field[field] = next_value_list;
  }

  return next_filter_by_field;
};

const areColumnFiltersEqual = (left_filter_by_field, right_filter_by_field) => {
  const left_field_list = Object.keys(left_filter_by_field);
  const right_field_list = Object.keys(right_filter_by_field);

  if (left_field_list.length !== right_field_list.length) {
    return false;
  }

  return left_field_list.every((field) => {
    const left_value_list = left_filter_by_field[field] || [];
    const right_value_list = right_filter_by_field[field] || [];

    return left_value_list.length === right_value_list.length
      && left_value_list.every((value, index) => value === right_value_list[index]);
  });
};

const criteriaWithoutColumnFilter = (
  criteria,
  filter_by_field,
  field,
  restored_criteria
) => {
  if (!field || !Object.prototype.hasOwnProperty.call(filter_by_field, field)) {
    return criteria;
  }

  const next_criteria = {
    ...criteria,
  };

  delete next_criteria[field];

  if (
    restored_criteria
    && Object.prototype.hasOwnProperty.call(restored_criteria, field)
  ) {
    next_criteria[field] = restored_criteria[field];
  }

  return next_criteria;
};

let company_id_list_promise = null;
let company_id_list_request_id = 0;
let contact_id_list_promise = null;
let contact_id_list_request_id = 0;
let bookmark_lock_token = 0;

const bookmark_lock_token_by_entity_id = new Map();
const bookmark_locked_entity_id_set = new Set();

const bookmarkEntityTypeId = (entity_type) => {
  if (entity_type === MY_LIST_CONSTANTS.ENTITY_TYPES.CONTACT) {
    return MY_LIST_CONSTANTS.BOOKMARK_ENTITY_TYPES.CONTACT;
  }

  if (entity_type === MY_LIST_CONSTANTS.ENTITY_TYPES.COMPANY) {
    return MY_LIST_CONSTANTS.BOOKMARK_ENTITY_TYPES.COMPANY;
  }

  return null;
};

const bookmarkMapFromList = (bookmark_list) => {
  const bookmark_by_entity_id = {};

  (bookmark_list ?? []).forEach((bookmark) => {
    bookmark_by_entity_id[bookmark.entity_id] = bookmark.id;
  });

  return bookmark_by_entity_id;
};

const applyBookmarksToList = (state, list_key) => {
  (state[list_key] ?? []).forEach((entity) => {
    if (bookmark_locked_entity_id_set.has(Number(entity.id))) {
      return;
    }

    const bookmark_id = state.bookmark_by_entity_id[entity.id] ?? null;

    entity.is_bookmarked = Boolean(bookmark_id);
    entity.bookmark_id = bookmark_id;
  });
};

const lockBookmarkEntity = (entity_id) => {
  const entity_key = Number(entity_id);

  bookmark_lock_token += 1;
  bookmark_lock_token_by_entity_id.set(entity_key, bookmark_lock_token);
  bookmark_locked_entity_id_set.add(entity_key);

  return bookmark_lock_token;
};

const unlockBookmarkEntity = (entity_id, token) => {
  const entity_key = Number(entity_id);

  if (bookmark_lock_token_by_entity_id.get(entity_key) !== token) {
    return;
  }

  bookmark_lock_token_by_entity_id.delete(entity_key);
  bookmark_locked_entity_id_set.delete(entity_key);
};

const resetBookmarkSyncState = () => {
  bookmark_locked_entity_id_set.clear();
  bookmark_lock_token_by_entity_id.clear();
};

const findBookmarkId = async (user_id, criteria) => {
  const response = await query_repository.searchBookmarks(user_id, criteria);
  const bookmark = response?.bookmark_list?.[0];

  return bookmark?.id ?? null;
};

const buildSearchQuery = (search_query, options) => {
  const built_search_query = { ...search_query };

  if (!options) {
    return built_search_query;
  }

  if (options.start_date) {
    built_search_query.interaction_created_date_lower_boundary = parseOptionDate(options.start_date)
      .startOf('day')
      .toUTC()
      .toISO();
  }

  if (options.end_date) {
    built_search_query.interaction_created_date_upper_boundary = parseOptionDate(options.end_date)
      .endOf('day')
      .toUTC()
      .toISO();
  }

  if (options.interaction_type_id_list) {
    built_search_query.interaction_type_id_list = options.interaction_type_id_list;
  }

  return built_search_query;
};

const state = () => ({
  account_column_filter_by_field: {},
  account_count: 0,
  account_list: [],
  account_quicksearch: '',
  account_sort_list: [],
  company_id_list: [],
  accounts_error: null,
  accounts_have_more: true,
  accounts_loading: false,
  accounts_offset: 0,
  bookmark_by_entity_id: {},
  bookmark_filter_enabled: false,
  bookmark_load_error: false,
  company_count: 0,
  contact_column_filter_by_field: {},
  contact_count: 0,
  contact_id_list: [],
  contact_list: [],
  contact_quicksearch: '',
  contacts_error: null,
  contacts_have_more: true,
  contacts_loading: false,
  contacts_offset: 0,
  contact_sort_list: [],
  entity_type: null,
  list_error: null,
  list_loading: false,
  list_name: null,
  list_request_id: 0,
  list_source: null,
  owner_user_id: null,
  query_id: null,
  query_information: null,
  search_query: null,
});

const getters = {
  accountColumnFilterByField: (state) => state.account_column_filter_by_field,
  accountCount: (state) => state.account_count,
  accountList: (state) => state.account_list,
  accountSort: (state) => state.account_sort_list[0] ?? null,
  companyIdList: (state) => state.company_id_list,
  accountsError: (state) => state.accounts_error,
  accountsHaveMore: (state) => state.accounts_have_more,
  accountsLoading: (state) => state.accounts_loading,
  bookmarkFilterEnabled: (state) => state.bookmark_filter_enabled,
  bookmarkLoadError: (state) => state.bookmark_load_error,
  companyCount: (state) => state.company_count,
  contactColumnFilterByField: (state) => state.contact_column_filter_by_field,
  contactCount: (state) => state.contact_count,
  contactIdList: (state) => state.contact_id_list,
  contactList: (state) => state.contact_list,
  contactSort: (state) => state.contact_sort_list[0] ?? null,
  contactsError: (state) => state.contacts_error,
  contactsHaveMore: (state) => state.contacts_have_more,
  contactsLoading: (state) => state.contacts_loading,
  entityType: (state) => state.entity_type,
  isIgnoreList: (state) => isIgnoreList(state.query_information),
  isListModified: (state) => isListModified(state.query_information),
  isQueryShared: (state) => isQueryShared(state.query_information),
  isSharedList: (state) => (
    isQueryShared(state.query_information) || isIgnoreList(state.query_information)
  ),
  listError: (state) => state.list_error,
  listLoading: (state) => state.list_loading,
  listName: (state) => state.list_name,
  listSource: (state) => state.list_source,
  ownerUserId: (state) => state.owner_user_id,
  queryId: (state) => state.query_id,
  queryInformation: (state) => state.query_information,
  searchQuery: (state) => state.search_query,
};

const actions = {
  async applyAccountQuicksearch({ commit, dispatch, state }, quicksearch) {
    const next_quicksearch = quicksearch || '';

    if (next_quicksearch === state.account_quicksearch) {
      return null;
    }

    const was_active = state.account_quicksearch.length >= QUICKSEARCH_MIN_LENGTH;
    const is_active = next_quicksearch.length >= QUICKSEARCH_MIN_LENGTH;

    commit('set_account_quicksearch', next_quicksearch);

    if (!was_active && !is_active) {
      return null;
    }

    return dispatch('reloadCurrentAccounts');
  },
  async applyAccountSort({ commit, dispatch, state }, sort_value) {
    if (state.account_sort_list[0] === sort_value) {
      return null;
    }

    commit('set_account_sort_list', [sort_value]);
    commit('reset_account_page');

    return dispatch('loadMoreAccounts');
  },
  async applyAccountColumnFilter({ commit, dispatch, state }, { field, value_list }) {
    if (!field) {
      return null;
    }

    const next_filter_by_field = columnFilterByField(
      state.account_column_filter_by_field,
      field,
      value_list
    );

    if (areColumnFiltersEqual(state.account_column_filter_by_field, next_filter_by_field)) {
      return null;
    }

    commit('set_account_column_filter_by_field', next_filter_by_field);

    return dispatch('reloadCurrentAccounts');
  },
  async clearAccountColumnFilters({ commit, dispatch, state }) {
    if (Object.keys(state.account_column_filter_by_field).length === 0) {
      return null;
    }

    commit('set_account_column_filter_by_field', {});

    return dispatch('reloadCurrentAccounts');
  },
  async clearAccountSort({ commit, dispatch, state }) {
    if (state.account_sort_list.length === 0) {
      return null;
    }

    commit('set_account_sort_list', []);
    commit('reset_account_page');

    return dispatch('loadMoreAccounts');
  },
  async applyContactQuicksearch({ commit, dispatch, state }, quicksearch) {
    const next_quicksearch = quicksearch || '';

    if (next_quicksearch === state.contact_quicksearch) {
      return null;
    }

    const was_active = state.contact_quicksearch.length >= QUICKSEARCH_MIN_LENGTH;
    const is_active = next_quicksearch.length >= QUICKSEARCH_MIN_LENGTH;

    commit('set_contact_quicksearch', next_quicksearch);

    if (!was_active && !is_active) {
      return null;
    }

    return dispatch('reloadCurrentContacts');
  },
  async applyContactSort({ commit, dispatch, state }, sort_value) {
    if (state.contact_sort_list[0] === sort_value) {
      return null;
    }

    commit('set_contact_sort_list', [sort_value]);
    commit('reset_contact_page');

    return dispatch('loadMoreContacts');
  },
  async applyContactColumnFilter({ commit, dispatch, state }, { field, value_list }) {
    if (!field) {
      return null;
    }

    const next_filter_by_field = columnFilterByField(
      state.contact_column_filter_by_field,
      field,
      value_list
    );

    if (areColumnFiltersEqual(state.contact_column_filter_by_field, next_filter_by_field)) {
      return null;
    }

    commit('set_contact_column_filter_by_field', next_filter_by_field);

    return dispatch('reloadCurrentContacts');
  },
  async clearContactColumnFilters({ commit, dispatch, state }) {
    if (Object.keys(state.contact_column_filter_by_field).length === 0) {
      return null;
    }

    commit('set_contact_column_filter_by_field', {});

    return dispatch('reloadCurrentContacts');
  },
  async clearContactSort({ commit, dispatch, state }) {
    if (state.contact_sort_list.length === 0) {
      return null;
    }

    commit('set_contact_sort_list', []);
    commit('reset_contact_page');

    return dispatch('loadMoreContacts');
  },
  async setBookmarkFilter({ commit, dispatch, state }, enabled) {
    const next_enabled = Boolean(enabled);

    if (next_enabled === state.bookmark_filter_enabled) {
      return null;
    }

    commit('set_bookmark_filter_enabled', next_enabled);

    if (state.entity_type === MY_LIST_CONSTANTS.ENTITY_TYPES.CONTACT) {
      return dispatch('reloadCurrentContacts');
    }

    if (state.entity_type === MY_LIST_CONSTANTS.ENTITY_TYPES.COMPANY) {
      return dispatch('reloadCurrentAccounts');
    }

    return null;
  },
  async toggleBookmarkFilter({ dispatch, state }) {
    return dispatch('setBookmarkFilter', !state.bookmark_filter_enabled);
  },
  async searchCompanyColumnValues({ state }, { column, field, value }) {
    const criteria = buildColumnValueCriteria(
      buildAccountSearchCriteria(state, {
        with_matched_queries: true,
      }),
      column,
      value
    );
    const restored_criteria = state.account_quicksearch.length >= QUICKSEARCH_MIN_LENGTH
      ? { company_name_list: [state.account_quicksearch] }
      : {};
    const response = await aggregator_user_data_repository.searchCompanyColumnValues(
      criteriaWithoutColumnFilter(
        criteria,
        state.account_column_filter_by_field,
        field,
        restored_criteria
      )
    );

    return Array.isArray(response?.value_list) ? response.value_list : [];
  },
  async searchContactColumnValues({ state }, { column, field, value }) {
    const criteria = buildColumnValueCriteria(
      buildContactSearchCriteria(state, {
        with_matched_queries: true,
      }),
      column,
      value
    );
    const restored_criteria = state.contact_quicksearch.length >= QUICKSEARCH_MIN_LENGTH
      ? { contact_quicksearch_list: [state.contact_quicksearch] }
      : {};
    const response = await aggregator_user_data_repository.searchContactColumnValues(
      criteriaWithoutColumnFilter(
        criteria,
        state.contact_column_filter_by_field,
        field,
        restored_criteria
      )
    );

    return Array.isArray(response?.value_list) ? response.value_list : [];
  },
  async reloadCurrentContacts({ commit, dispatch }) {
    commit('reset_contact_page');

    return dispatch('loadMoreContacts');
  },
  async removeContactsFromCurrentList({ commit, dispatch, state }, contact_id_list) {
    const query_id = state.query_id;
    const criteria = parseQueryCriteria(state.query_information?.criteria);
    const id_key = criteriaContactIdKey(criteria);

    if (!query_id || !id_key || !Array.isArray(contact_id_list) || contact_id_list.length === 0) {
      throw new Error('Missing contact list criteria');
    }

    const contact_id_set = new Set(contact_id_list.map((id) => String(id)));
    const next_criteria = {
      ...criteria,
      [id_key]: criteria[id_key].filter((id) => !contact_id_set.has(String(id))),
    };

    await dispatch('query/updateQuery', {
      data: {
        criteria: next_criteria,
      },
      query_id,
    }, { root: true });

    commit('set_query_information', {
      ...state.query_information,
      criteria: next_criteria,
    });

    await new Promise((resolve) => {
      setTimeout(resolve, REMOVE_CONTACT_REFRESH_DELAY);
    });

    return dispatch('reloadCurrentContacts');
  },
  async removeCompaniesFromCurrentList({ commit, dispatch, state }, company_id_list) {
    const query_id = state.query_id;
    const criteria = parseQueryCriteria(state.query_information?.criteria);

    if (
      !query_id
      || !Array.isArray(criteria.company_id_list)
      || !Array.isArray(company_id_list)
      || company_id_list.length === 0
    ) {
      throw new Error('Missing company list criteria');
    }

    const company_id_set = new Set(company_id_list.map((id) => String(id)));
    const next_criteria = {
      ...criteria,
      company_id_list: criteria.company_id_list
        .filter((id) => !company_id_set.has(String(id))),
    };

    await dispatch('query/updateQuery', {
      data: {
        criteria: next_criteria,
      },
      query_id,
    }, { root: true });

    commit('set_query_information', {
      ...state.query_information,
      criteria: next_criteria,
    });

    await new Promise((resolve) => {
      setTimeout(resolve, REMOVE_CONTACT_REFRESH_DELAY);
    });

    return dispatch('reloadCurrentAccounts');
  },
  async initializeList({ commit, dispatch, state }, input) {
    commit('reset_list');
    commit('set_list_source', MY_LIST_CONSTANTS.LIST_SOURCES.QUERY);
    commit('set_list_identifiers', input);
    commit('set_search_query', buildSearchQuery({ query_id_list: [input.query_id] }));

    const list_request_id = state.list_request_id;
    const query_information = await dispatch('loadQueryInformation');

    if (state.list_request_id !== list_request_id) {
      return null;
    }

    commit('set_list_name', query_information?.name ?? null);

    const entity_type = resolveEntityType(
      query_information?.query_type?.id ?? query_information?.query_type_id
    );
    const entities = await dispatch('loadFirstEntityPage', entity_type);

    return {
      entities,
      entity_type,
      query_information,
    };
  },
  async initializeParameterizedList({ commit, dispatch }, input) {
    commit('reset_list');
    commit('set_list_source', MY_LIST_CONSTANTS.LIST_SOURCES.PARAMETERIZED);
    commit('set_list_identifiers', {
      owner_user_id: input.owner_user_id ?? null,
      query_id: null,
    });
    commit('set_list_name', input.name ?? null);
    commit('set_search_query', buildSearchQuery(input.search_query, input.options));

    const entity_type = resolveEntityType(input.query_type_id);
    const entities = await dispatch('loadFirstEntityPage', entity_type);

    return {
      entities,
      entity_type,
    };
  },
  loadFirstEntityPage({ commit, dispatch }, entity_type) {
    if (!entity_type) {
      commit('set_list_error', new Error('Not supported query type for my lists'));

      return null;
    }

    commit('set_entity_type', entity_type);

    if (entity_type === MY_LIST_CONSTANTS.ENTITY_TYPES.CONTACT) {
      return dispatch('loadMoreContacts');
    }

    return dispatch('loadMoreAccounts');
  },
  async loadQueryInformation({ commit, state }) {
    const list_request_id = state.list_request_id;
    const owner_user_id = state.owner_user_id;
    const query_id = state.query_id;

    commit('set_list_loading', true);
    commit('set_list_error', null);

    try {
      const query_information = await query_repository.readQuery(owner_user_id, query_id);

      if (state.list_request_id === list_request_id) {
        commit('set_query_information', query_information);
      }

      return query_information;
    } catch (error) {
      if (state.list_request_id === list_request_id) {
        commit('set_list_error', error);
      }

      throw error;
    } finally {
      if (state.list_request_id === list_request_id) {
        commit('set_list_loading', false);
      }
    }
  },
  async refreshCurrentListInformation({ commit, dispatch }) {
    const query_information = await dispatch('loadQueryInformation');

    commit('set_list_name', query_information?.name ?? null);

    return query_information;
  },
  async loadCompanyIdList({ commit, state }) {
    if (!state.search_query || state.account_count < 1) {
      return [];
    }

    if (state.company_id_list.length >= state.account_count) {
      return state.company_id_list;
    }

    if (
      company_id_list_promise
      && company_id_list_request_id === state.list_request_id
    ) {
      return company_id_list_promise;
    }

    const list_request_id = state.list_request_id;

    company_id_list_request_id = list_request_id;
    company_id_list_promise = fetchAllCompanyIds(state)
      .then((company_id_list) => {
        if (state.list_request_id !== list_request_id) {
          return state.company_id_list;
        }

        commit('set_company_id_list', company_id_list);

        return company_id_list;
      })
      .finally(() => {
        if (company_id_list_request_id === list_request_id) {
          company_id_list_promise = null;
        }
      });

    return company_id_list_promise;
  },
  async loadContactIdList({ commit, state }) {
    if (!state.search_query || state.contact_count < 1) {
      return [];
    }

    if (state.contact_id_list.length >= state.contact_count) {
      return state.contact_id_list;
    }

    if (
      contact_id_list_promise
      && contact_id_list_request_id === state.list_request_id
    ) {
      return contact_id_list_promise;
    }

    const list_request_id = state.list_request_id;

    contact_id_list_request_id = list_request_id;
    contact_id_list_promise = fetchAllContactIds(state)
      .then((contact_id_list) => {
        if (state.list_request_id !== list_request_id) {
          return state.contact_id_list;
        }

        commit('set_contact_id_list', contact_id_list);

        return contact_id_list;
      })
      .finally(() => {
        if (contact_id_list_request_id === list_request_id) {
          contact_id_list_promise = null;
        }
      });

    return contact_id_list_promise;
  },
  async loadMoreContacts({ commit, dispatch, state }) {
    if (state.contacts_loading || !state.contacts_have_more || !state.search_query) {
      return null;
    }

    const list_request_id = state.list_request_id;
    const offset = state.contacts_offset;
    const should_load_bookmarks = offset === 0 && Boolean(state.query_id);
    const bookmarks_promise = should_load_bookmarks
      ? dispatch('loadBookmarks')
      : null;

    if (should_load_bookmarks) {
      commit('set_bookmark_load_error', false);
    }

    commit('set_contacts_loading', true);
    commit('set_contacts_error', null);

    try {
      const response = await aggregator_user_data_repository.searchContacts(
        buildContactSearchCriteria(state, {
          limit: ENTITY_PAGE_SIZE,
          offset,
        })
      );

      if (state.list_request_id !== list_request_id) {
        bookmarks_promise?.catch(() => null);

        return response;
      }

      const contact_list = response.contact_list ?? [];
      const contact_count = response.contact_count ?? 0;
      const company_count = response.company_count ?? 0;
      const next_offset = offset + contact_list.length;

      commit('append_contact_list', contact_list);
      commit('set_contact_count', contact_count);
      commit('set_company_count', company_count);
      commit('set_contacts_offset', next_offset);
      commit(
        'set_contacts_have_more',
        contact_list.length === ENTITY_PAGE_SIZE && next_offset < contact_count
      );

      if (offset === 0 && contact_count > 0) {
        dispatch('loadContactIdList').catch(() => null);
      }

      if (bookmarks_promise) {
        try {
          await bookmarks_promise;
        } catch {
          if (state.list_request_id === list_request_id) {
            commit('set_bookmark_load_error', true);
          }
        }
      }

      return response;
    } catch (error) {
      bookmarks_promise?.catch(() => null);

      if (state.list_request_id === list_request_id) {
        commit('set_contacts_error', error);
      }

      throw error;
    } finally {
      if (state.list_request_id === list_request_id) {
        commit('set_contacts_loading', false);
      }
    }
  },
  async reloadCurrentAccounts({ commit, dispatch }) {
    commit('reset_account_page');

    return dispatch('loadMoreAccounts');
  },
  async loadMoreAccounts({ commit, dispatch, state }) {
    if (state.accounts_loading || !state.accounts_have_more || !state.search_query) {
      return null;
    }

    const list_request_id = state.list_request_id;
    const offset = state.accounts_offset;
    const should_load_bookmarks = offset === 0 && Boolean(state.query_id);
    const bookmarks_promise = should_load_bookmarks
      ? dispatch('loadBookmarks')
      : null;

    if (should_load_bookmarks) {
      commit('set_bookmark_load_error', false);
    }

    commit('set_accounts_loading', true);
    commit('set_accounts_error', null);

    try {
      const response = await aggregator_user_data_repository.searchCompaniesSimple(
        buildAccountSearchCriteria(state, {
          limit: ENTITY_PAGE_SIZE,
          offset,
          with_matched_queries: true,
        })
      );

      if (state.list_request_id !== list_request_id) {
        bookmarks_promise?.catch(() => null);

        return response;
      }

      const account_list = response.company_list ?? [];
      const account_count = response.company_count ?? 0;
      const next_offset = offset + account_list.length;

      commit('append_account_list', account_list);
      commit('set_account_count', account_count);
      commit('set_accounts_offset', next_offset);
      commit(
        'set_accounts_have_more',
        account_list.length === ENTITY_PAGE_SIZE && next_offset < account_count
      );

      if (offset === 0 && account_count > 0) {
        dispatch('loadCompanyIdList').catch(() => null);
      }

      if (bookmarks_promise) {
        try {
          await bookmarks_promise;
        } catch {
          if (state.list_request_id === list_request_id) {
            commit('set_bookmark_load_error', true);
          }
        }
      }

      return response;
    } catch (error) {
      bookmarks_promise?.catch(() => null);

      if (state.list_request_id === list_request_id) {
        commit('set_accounts_error', error);
        commit('set_accounts_have_more', false);
      }

      throw error;
    } finally {
      if (state.list_request_id === list_request_id) {
        commit('set_accounts_loading', false);
      }
    }
  },
  async loadBookmarks({ commit, rootState, state }) {
    const user_id = rootState.user?.token_data?.user_id;
    const query_id = state.query_id;
    const entity_type = bookmarkEntityTypeId(state.entity_type);

    if (!user_id || !query_id || !entity_type) {
      return;
    }

    const list_request_id = state.list_request_id;
    const response = await query_repository.searchBookmarks(user_id, {
      entity_type,
      query_id_list: [query_id],
    });

    if (state.list_request_id !== list_request_id) {
      return;
    }

    commit(
      'set_bookmark_by_entity_id',
      bookmarkMapFromList(response?.bookmark_list)
    );
    commit('apply_bookmarks');
  },
  async toggleEntityBookmark({ commit, rootState, state }, payload) {
    const entity_id = payload?.entity_id;
    const entity_list_key = payload?.entity_list_key;
    const entity_type = payload?.entity_type;
    const user_id = rootState.user?.token_data?.user_id;
    const query_id = state.query_id;
    const entity = (state[entity_list_key] ?? [])
      .find((item) => Number(item.id) === Number(entity_id));

    if (!user_id || !query_id || !entity || entity.bookmark_pending) {
      return;
    }

    const list_request_id = state.list_request_id;
    const lock_token = lockBookmarkEntity(entity_id);
    const previous_bookmark_id = entity.bookmark_id ?? null;
    const previous_is_bookmarked = Boolean(entity.is_bookmarked);
    const is_same_list = () => state.list_request_id === list_request_id;

    commit('set_entity_bookmark_pending', {
      bookmark_pending: true,
      entity_id,
      entity_list_key,
    });
    commit('set_entity_bookmarked', {
      entity_id,
      entity_list_key,
      is_bookmarked: !previous_is_bookmarked,
    });

    try {
      if (previous_is_bookmarked) {
        const bookmark_id = previous_bookmark_id ?? await findBookmarkId(user_id, {
          entity_id_list: [entity_id],
          entity_type,
          query_id_list: [query_id],
        });

        if (!bookmark_id) {
          throw new Error('Missing bookmark id');
        }

        await query_repository.deleteBookmark(user_id, bookmark_id);

        if (!is_same_list()) {
          return;
        }

        commit('set_entity_bookmarked', {
          bookmark_id: null,
          entity_id,
          entity_list_key,
          is_bookmarked: false,
        });

        return;
      }

      const bookmark = await query_repository.createBookmark(user_id, {
        entity_id: Number(entity_id),
        entity_type,
        query_id: Number(query_id),
      });

      if (!is_same_list()) {
        return;
      }

      commit('set_entity_bookmarked', {
        bookmark_id: bookmark.id,
        entity_id,
        entity_list_key,
        is_bookmarked: true,
      });
    } catch (error) {
      if (!previous_is_bookmarked && error?.response?.status === 400) {
        try {
          const bookmark_id = await findBookmarkId(user_id, {
            entity_id_list: [entity_id],
            entity_type,
            query_id_list: [query_id],
          });

          if (bookmark_id && is_same_list()) {
            commit('set_entity_bookmarked', {
              bookmark_id,
              entity_id,
              entity_list_key,
              is_bookmarked: true,
            });

            return;
          }
        } catch {
          // The lookup failed, the bookmark state is rolled back below.
        }
      }

      if (is_same_list()) {
        commit('set_entity_bookmarked', {
          bookmark_id: previous_bookmark_id,
          entity_id,
          entity_list_key,
          is_bookmarked: previous_is_bookmarked,
        });
      }

      throw error;
    } finally {
      if (is_same_list()) {
        commit('set_entity_bookmark_pending', {
          bookmark_pending: false,
          entity_id,
          entity_list_key,
        });
      }

      unlockBookmarkEntity(entity_id, lock_token);
    }
  },
  resetList({ commit }) {
    commit('reset_list');
  },
};

const mutations = {
  append_account_list(state, account_list) {
    const existing_account_id_set = new Set(state.account_list.map((account) => account.id));
    const new_account_list = account_list
      .filter((account) => !existing_account_id_set.has(account.id));

    state.account_list = state.account_list.concat(new_account_list);
    applyBookmarksToList(state, 'account_list');
  },
  append_contact_list(state, contact_list) {
    const existing_contact_id_set = new Set(state.contact_list.map((contact) => contact.id));
    const new_contact_list = contact_list
      .filter((contact) => !existing_contact_id_set.has(contact.id));

    state.contact_list = state.contact_list.concat(new_contact_list);
    applyBookmarksToList(state, 'contact_list');
  },
  apply_bookmarks(state) {
    if (state.entity_type === MY_LIST_CONSTANTS.ENTITY_TYPES.CONTACT) {
      applyBookmarksToList(state, 'contact_list');

      return;
    }

    if (state.entity_type === MY_LIST_CONSTANTS.ENTITY_TYPES.COMPANY) {
      applyBookmarksToList(state, 'account_list');
    }
  },
  reset_account_page(state) {
    resetBookmarkSyncState();
    state.list_request_id += 1;
    state.account_list = [];
    state.accounts_error = null;
    state.accounts_have_more = true;
    state.accounts_loading = false;
    state.accounts_offset = 0;
    state.company_id_list = [];
  },
  reset_contact_page(state) {
    resetBookmarkSyncState();
    state.list_request_id += 1;
    state.contact_id_list = [];
    state.contact_list = [];
    state.contacts_error = null;
    state.contacts_have_more = true;
    state.contacts_loading = false;
    state.contacts_offset = 0;
  },
  reset_list(state) {
    const list_request_id = state.list_request_id + 1;

    resetBookmarkSyncState();
    Object.assign(state, {
      account_column_filter_by_field: {},
      account_count: 0,
      account_list: [],
      account_quicksearch: '',
      account_sort_list: [],
      company_id_list: [],
      accounts_error: null,
      accounts_have_more: true,
      accounts_loading: false,
      accounts_offset: 0,
      bookmark_by_entity_id: {},
      bookmark_filter_enabled: false,
      bookmark_load_error: false,
      company_count: 0,
      contact_column_filter_by_field: {},
      contact_count: 0,
      contact_id_list: [],
      contact_list: [],
      contact_quicksearch: '',
      contacts_error: null,
      contacts_have_more: true,
      contacts_loading: false,
      contacts_offset: 0,
      contact_sort_list: [],
      entity_type: null,
      list_error: null,
      list_loading: false,
      list_name: null,
      list_request_id,
      list_source: null,
      owner_user_id: null,
      query_id: null,
      query_information: null,
      search_query: null,
    });
  },
  set_company_id_list(state, company_id_list) {
    state.company_id_list = company_id_list;
  },
  set_account_column_filter_by_field(state, account_column_filter_by_field) {
    state.account_column_filter_by_field = account_column_filter_by_field;
  },
  set_account_quicksearch(state, account_quicksearch) {
    state.account_quicksearch = account_quicksearch;
  },
  set_account_sort_list(state, account_sort_list) {
    state.account_sort_list = account_sort_list;
  },
  set_account_count(state, account_count) {
    state.account_count = account_count;
  },
  set_accounts_error(state, accounts_error) {
    state.accounts_error = accounts_error;
  },
  set_accounts_have_more(state, accounts_have_more) {
    state.accounts_have_more = accounts_have_more;
  },
  set_accounts_loading(state, accounts_loading) {
    state.accounts_loading = accounts_loading;
  },
  set_accounts_offset(state, accounts_offset) {
    state.accounts_offset = accounts_offset;
  },
  set_company_count(state, company_count) {
    state.company_count = company_count;
  },
  set_contact_column_filter_by_field(state, contact_column_filter_by_field) {
    state.contact_column_filter_by_field = contact_column_filter_by_field;
  },
  set_contact_count(state, contact_count) {
    state.contact_count = contact_count;
  },
  set_contact_id_list(state, contact_id_list) {
    state.contact_id_list = contact_id_list;
  },
  set_bookmark_filter_enabled(state, bookmark_filter_enabled) {
    state.bookmark_filter_enabled = Boolean(bookmark_filter_enabled);
  },
  set_bookmark_by_entity_id(state, bookmark_by_entity_id) {
    const next_bookmark_by_entity_id = {
      ...bookmark_by_entity_id,
    };

    bookmark_locked_entity_id_set.forEach((entity_id) => {
      const bookmark_id = state.bookmark_by_entity_id[entity_id] ?? null;

      if (bookmark_id) {
        next_bookmark_by_entity_id[entity_id] = bookmark_id;
      } else {
        delete next_bookmark_by_entity_id[entity_id];
      }
    });

    state.bookmark_by_entity_id = next_bookmark_by_entity_id;
  },
  set_bookmark_load_error(state, bookmark_load_error) {
    state.bookmark_load_error = bookmark_load_error;
  },
  set_entity_bookmark_pending(state, payload) {
    const entity = (state[payload?.entity_list_key] ?? [])
      .find((item) => Number(item.id) === Number(payload?.entity_id));

    if (!entity) {
      return;
    }

    entity.bookmark_pending = Boolean(payload.bookmark_pending);
  },
  set_entity_bookmarked(state, payload) {
    const entity = (state[payload?.entity_list_key] ?? [])
      .find((item) => Number(item.id) === Number(payload?.entity_id));

    if (!entity) {
      return;
    }

    entity.is_bookmarked = Boolean(payload.is_bookmarked);

    if (!Object.prototype.hasOwnProperty.call(payload, 'bookmark_id')) {
      return;
    }

    entity.bookmark_id = payload.bookmark_id;

    const next_bookmark_by_entity_id = {
      ...state.bookmark_by_entity_id,
    };

    if (payload.bookmark_id) {
      next_bookmark_by_entity_id[payload.entity_id] = payload.bookmark_id;
    } else {
      delete next_bookmark_by_entity_id[payload.entity_id];
    }

    state.bookmark_by_entity_id = next_bookmark_by_entity_id;
  },
  set_contacts_query_lists(state, payload) {
    const added_query_list = payload?.added_query_list ?? [];
    const contact_id_set = new Set(payload?.contact_id_list ?? []);
    const removed_query_id_set = new Set(payload?.removed_query_id_list ?? []);

    state.contact_list.forEach((contact) => {
      if (!contact_id_set.has(contact.id)) {
        return;
      }

      const current_query_list = Array.isArray(contact.query_list)
        ? contact.query_list
        : [];
      const next_query_list = current_query_list
        .filter((query) => !removed_query_id_set.has(query.id));
      const existing_query_id_set = new Set(next_query_list.map((query) => query.id));

      added_query_list.forEach((query) => {
        if (!existing_query_id_set.has(query.id)) {
          next_query_list.push({
            id: query.id,
            name: query.name,
          });
        }
      });

      contact.query_list = next_query_list;
    });
  },
  set_contact_quicksearch(state, contact_quicksearch) {
    state.contact_quicksearch = contact_quicksearch;
  },
  set_contact_sort_list(state, contact_sort_list) {
    state.contact_sort_list = contact_sort_list;
  },
  set_contacts_error(state, contacts_error) {
    state.contacts_error = contacts_error;
  },
  set_contacts_have_more(state, contacts_have_more) {
    state.contacts_have_more = contacts_have_more;
  },
  set_contacts_loading(state, contacts_loading) {
    state.contacts_loading = contacts_loading;
  },
  set_contacts_offset(state, contacts_offset) {
    state.contacts_offset = contacts_offset;
  },
  set_entity_type(state, entity_type) {
    state.entity_type = entity_type;
  },
  set_list_error(state, list_error) {
    state.list_error = list_error;
  },
  set_list_identifiers(state, input) {
    state.owner_user_id = input.owner_user_id;
    state.query_id = input.query_id;
  },
  set_list_loading(state, list_loading) {
    state.list_loading = list_loading;
  },
  set_list_name(state, list_name) {
    state.list_name = list_name;
  },
  set_list_source(state, list_source) {
    state.list_source = list_source;
  },
  set_query_information(state, query_information) {
    state.query_information = query_information;
  },
  set_search_query(state, search_query) {
    state.search_query = search_query;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
